<template>
  <div class="relative flex flex-col h-full gap-4 pt-4">
    <!-- 16.5rem  -->
    <div v-if="updateLoading" class="absolute z-10 -top-6 left-16">
      <div class="blum-loader-btn"></div>
    </div>
    <div class="flex flex-col gap-4 w-160">
      <BlInputText :input="nameInput" @changed="inputHandler" />
      <BlSelectBox
        label="Curriculum"
        asterisk
        search
        v-model="curriculumIdInput"
        placeholder="Select curriculum"
        :options="curriculumOptions"
        @change="onChangeCurriculum"
      />
      <BlInputText :input="descriptionInput" @changed="inputHandler" />
    </div>
    <div class="w-full">
      <div
        class="flex items-center flex-shrink-0 w-full h-10 px-5 bg-black rounded"
      >
        <div class="text-sm text-white">Tasks</div>
      </div>
      <div class="overflow-x-auto scrollbar-lg" :style="{ minWidth: '100%' }">
        <div class="flex flex-col min-w-min">
          <div
            class="overflow-x-auto overflow-y-auto scrollbar-lg"
            :style="{
              height:
                'calc(100vh - 4.5rem - 1rem - 3rem - 1.5rem - 1rem - 16.5rem - 1rem - 2.5rem)'
            }"
          >
            <div
              v-if="dataSteps.length"
              class="sticky top-0 z-20 flex min-h-0 bg-white w-min"
            >
              <div
                class="sticky top-0 left-0 z-20 bg-white border-b border-r border-black w-72"
              ></div>
              <div
                v-for="routine in dataRoutines"
                :key="routine.id"
                class="relative flex items-center h-16 pl-2 border-b border-r border-black w-52"
              >
                <custom-input
                  :value="routine.date"
                  :type="'date'"
                  v-on:update:value="
                    routine.date = $event;
                    editRoutine(routine);
                  "
                  class="text-sm w-44"
                />
                <div
                  v-popover="{ name: `remove-routine-${routine.id}` }"
                  class="flex items-center justify-center w-6 text-xs text-gray-600 rounded cursor-pointer"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </div>
                <popover
                  :name="`remove-routine-${routine.id}`"
                  :width="128"
                  :pointer="false"
                  class="absolute left-16 top-12"
                >
                  <div
                    class="w-full px-2 py-1 cursor-pointer hover:bg-hover-state"
                    @click="removeRoutine(routine)"
                  >
                    Delete
                  </div>
                </popover>
              </div>
              <div
                class="flex items-center w-64 h-16 gap-2 px-5 text-sm rounded cursor-pointer"
                @click="addDate"
              >
                <i class="fas fa-plus"></i>
                <div class="font-medium">Add Date</div>
              </div>
            </div>
            <div class="flex flex-col" :style="{ minHeight: '18rem' }">
              <draggable
                handle=".handle"
                @update="moveStep($event)"
                class="h-min"
              >
                <div
                  v-for="step in dataSteps"
                  :key="step.id"
                  :id="step.id"
                  class="flex w-min"
                >
                  <div
                    class="sticky left-0 z-10 flex items-center h-16 min-h-0 bg-white border-b border-r border-black w-72"
                  >
                    <div class="px-3 text-sm text-black cursor-move handle">
                      <i class="fas fa-grip-vertical"></i>
                    </div>
                    <textarea-autosize
                      :value="step.name"
                      :placeholder="'Task Title'"
                      :max-height="120"
                      :min-height="20"
                      class="w-full px-2 py-1 text-sm leading-tight bg-transparent border-none rounded text-secondary3 placeholder-placeholder focus:outline"
                      @blur.native="editStep(step, $event)"
                    />
                    <div class="relative">
                      <div
                        v-popover.left="{ name: `remove-step-${step.id}` }"
                        class="px-3 text-xs text-gray-600 cursor-pointer"
                      >
                        <i class="fas fa-ellipsis-v"></i>
                      </div>
                      <popover
                        :name="`remove-step-${step.id}`"
                        :width="128"
                        :pointer="false"
                        class="absolute -top-4 -left-32"
                      >
                        <div
                          class="px-2 py-1 text-left cursor-pointer hover:bg-hover-state"
                          @click="removeStep(step)"
                        >
                          Delete
                        </div>
                      </popover>
                    </div>
                  </div>
                  <div
                    v-for="routine in dataRoutines"
                    :key="routine.id"
                    class="flex items-center min-h-0 px-2 py-2 border-b border-r border-black w-52"
                    :class="{
                      'bg-prim-400': routine.prompts[step.id] === 'Independent'
                    }"
                  >
                    <BlSelectBox
                      dense
                      borderless
                      v-model="routine.prompts[step.id]"
                      placeholder="Select prompt"
                      :options="promptOptions"
                      @change="onChangePrompt(step, routine, $event)"
                    />
                  </div>
                </div>
              </draggable>
              <div class="flex w-min">
                <div
                  class="sticky left-0 flex items-center w-64 h-16 gap-2 px-5 text-sm bg-white rounded cursor-pointer"
                  @click="addItem"
                >
                  <i class="fas fa-plus"></i>
                  <div class="font-medium">Add Item</div>
                </div>
              </div>
            </div>
            <div class="sticky bottom-0 z-20 flex bg-white">
              <div
                class="flex bg-white border-t-2 border-b border-black min-w-min"
              >
                <div
                  class="sticky left-0 z-10 flex items-center px-4 bg-white border-r border-black h-15 w-72"
                >
                  Percentage
                </div>
                <div
                  v-for="routine in dataRoutines"
                  :key="routine.id"
                  class="flex items-center px-4 border-r border-black h-15 w-52"
                >
                  {{ calculatePercentage(routine) }} %
                </div>
                <div v-if="dataRoutines.length > 2" class="w-64 h-15"></div>
              </div>
              <div
                v-if="dataRoutines.length < 3"
                class="w-full border-t-2 border-b border-black h-15"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import draggable from "vuedraggable";
import debounce from "lodash/debounce";

import BlSelectBox from "../BlSelectBox.vue";
import BlInputText from "../BlInputText.vue";
//
import CustomInput from "../CustomInput.vue";
import CustomSelect from "../CustomSelect.vue";

export default {
  props: ["selectedTask", "curriculumOptions"],
  components: {
    draggable,
    BlSelectBox,
    BlInputText,
    CustomInput,
    CustomSelect
  },
  data() {
    return {
      updateLoading: false,
      nameInput: {
        label: "Title",
        placeholder: "Type title task",
        model: "",
        asterisk: true
      },
      curriculumIdInput: "",
      descriptionInput: {
        label: "Description",
        placeholder: "Type description task",
        model: "",
        textarea: true,
        asterisk: true
      },
      //
      label: "Prompt",
      code: "prompt",
      promptOptions: [
        { label: "Independent", value: "Independent" },
        { label: "Model", value: "Model" },
        { label: "Gestural", value: "Gestural" },
        { label: "Partial Prompt", value: "Partial Prompt" },
        { label: "Full Prompt", value: "Full Prompt" }
      ]
    };
  },
  computed: {
    ...mapGetters(["tasks", "task", "steps", "routines"]),
    dataSteps() {
      var data = [...this.steps];
      data.sort(function(a, b) {
        return a.position - b.position;
      });
      return data;
    },
    dataRoutines() {
      let data = [...this.routines];
      data.sort(function(a, b) {
        return a.id - b.id;
      });
      return data;
    }
  },
  mounted() {
    this.nameInput.model = this.selectedTask.name;
    this.curriculumIdInput = this.selectedTask.curriculum_id;
    this.descriptionInput.model = this.selectedTask.description;
  },
  methods: {
    ...mapActions([
      "updateTask",
      "createStep",
      "updateStep",
      "deleteStep",
      "createRoutine",
      "updateRoutine",
      "deleteRoutine",
      "insertRoutinePrompt"
    ]),
    inputHandler(payload) {
      if (payload.input.label === "Title") {
        if (this.nameInput.model === payload.value) return;
      }
      if (payload.input.label === "Description") {
        if (this.descriptionInput.model === payload.value) return;
      }

      this.updateLoading = true;
      payload.input.model = payload.value;
      this.editTask();
    },
    onChangeCurriculum(id) {
      if (this.curriculumIdInput === id) return;

      this.updateLoading = true;
      this.curriculumIdInput = id;
      this.editTask();
    },
    editTask: debounce(async function() {
      const params = {
        id: this.selectedTask.id,
        name: this.nameInput.model,
        curriculum_id: this.curriculumIdInput,
        description: this.descriptionInput.model
      };
      await this.updateTask(params);
      this.updateLoading = false;
    }, 1000),
    //
    addDate() {
      var today = new Date();
      var dd = String(today.getDate()).padStart(2, "0");
      var mm = String(today.getMonth() + 1).padStart(2, "0");
      var yyyy = today.getFullYear();
      this.createRoutine({
        id: this.selectedTask.id,
        params: {
          date: yyyy + "-" + mm + "-" + dd
        }
      });
    },
    addItem() {
      this.createStep({
        id: this.selectedTask.id,
        params: {
          name: "Task Title"
        }
      });
    },
    editStep: debounce(function(step, event) {
      const data = event.target.value;
      step.name = data;
      this.updateStep(step);
    }, 1000),
    editRoutine: debounce(function(routine) {
      this.updateRoutine(routine);
    }, 1000),
    //
    async onChangePrompt(step, routine, val) {
      const params = {
        id: step.id,
        routine_id: routine.id,
        prompt: val,
        task_id: routine.task_id
      };
      await this.insertRoutinePrompt(params);
    },
    removeStep(step) {
      if (confirm("Are you sure you want to delete this step?")) {
        this.deleteStep(step);
      }
    },
    removeRoutine(routine) {
      if (confirm("Are you sure you want to delete this routine?")) {
        this.deleteRoutine(routine);
      }
    },
    moveStep(event) {
      const step = this.dataSteps.find(
        item => item.id === parseInt(event.item.id)
      );
      step.position = event.newIndex + 1;
      this.updateStep(step);
    },
    calculatePercentage(routine) {
      var totalRoutine = this.dataSteps.length;
      var totalIndependent = 0;
      this.dataSteps.forEach(step => {
        if (routine.prompts[step.id] === "Independent") totalIndependent += 1;
      });
      return Math.round((totalIndependent * 100) / totalRoutine);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/TaskGoals.vue